import React, { useEffect, useState, useMemo } from "react";
import _ from "lodash";
import { useRouteMatch } from "react-router-dom";
import {
  createStyles,
  Grid,
  makeStyles,
  Typography,
  TextField,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import "jspdf-autotable";

import { CustomTheme } from "../../../../types/customTheme";
import WithLoading from "../../../../utils/WithLoading";
import BannerInfoNode from "./BannerInfoNode";
import BannerHeader from "./BannerHeader";
import CreateNewBanner from "./CreateNewBanner";
import BannerCreateAndUpdateModal from "./BannerCreateAndUpdateModal";
import {
  createNewBannerInfo,
  deleteBannerInfo,
  updateBannerInfo,
} from "../../../../services/customerApp/customerDetails";
import {
  SUCCESSFULLY_CREATED,
  SUCCESSFULLY_DELETED,
  SUCCESSFULLY_UPDATED,
} from "../../../../utils/consts";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import { getObjectDifferences } from "../../../../utils/isDeepEqual";
import ConfirmDeleteDialog from "../../../PrestoExpressEatprestoApp/components/dialogs/ConfirmDeleteDialog";

export interface CustomerNodeProps {
  isLoading: any;
  bannerNode: any;
  isOwner: any;
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    autoComplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: `1px solid ${theme.palette.background.entity_border}`,
        color: theme.palette.custom.orange.contrastText,
      },
    },
    titleColor: {
      color: theme.palette.custom.orange.contrastText,
    },
  }),
);

const BannerNode: React.FunctionComponent<CustomerNodeProps> = ({
  isLoading,
  bannerNode,
  isOwner,
}) => {
  const [bannerList, setBannerList] = useState<any>([]);
  const [error, setError] = useState("");
  const [isOpenBannerAddEditModal, setIsOpenBannerAddEditModal] =
    useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [description, setDescription] = useState("");
  const [img, setImg] = useState("");
  const [isError, setIsError] = useState(false);
  const [bannerId, setBannerId] = useState("");
  const [success, setSuccess] = useState("");
  const [isStatus, setIsStatus] = useState(true);
  const [order, setOrder] = useState(0);
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] =
    useState(false);
  const [originalNodeData, setOriginalNodeData] = useState({
    availability: [],
  });
  const [selectedFilter, setSelectedFilter] = useState<any>({
    value: "all",
    label: "All Banners",
  });
  const match: any = useRouteMatch();
  const classes = useStyles();

  // Filter options for the autocomplete
  const filterOptions = [
    { value: "all", label: "All Banners" },
    { value: "enabled", label: "Enabled Only" },
    { value: "disabled", label: "Disabled Only" },
  ];

  useEffect(() => {
    if (!_.isEmpty(bannerNode)) {
      setBannerList(bannerNode);
    }
  }, [bannerNode]);

  // Filter banners based on selected filter
  const filteredBannerList = useMemo(() => {
    if (selectedFilter?.value === "all") {
      return bannerList;
    }
    const isEnabled = selectedFilter?.value === "enabled";
    return bannerList.filter((banner: any) => {
      // Assuming 'status' field indicates if banner is enabled
      const bannerStatus =
        banner?.status?.toString() === "true" || banner?.enabled === true;
      return bannerStatus === isEnabled;
    });
  }, [bannerList, selectedFilter]);

  const handleFilterChange = (newValue: any) => {
    setSelectedFilter(newValue);
  };

  const handleOpenBannerAddModal = () => {
    setIsOpenBannerAddEditModal(true);
  };

  const handleReset = () => {
    setIsEdit(false);
    setIsOpenBannerAddEditModal(false);
    setBannerId("");
    setTitle("");
    setSubTitle("");
    setDescription("");
    setImg("");
    setIsStatus(true);
    setOrder(0);
    setOriginalNodeData({ availability: [] });
  };

  const handleCreateBanner = async () => {
    try {
      const formData = {
        title: title,
        subTitle: subTitle,
        description: description,
        img: img,
        status: isStatus,
        order: order,
        availability: originalNodeData.availability,
      };

      let message = "";
      let body = {};
      let summaryString = "";

      summaryString =
        "Banner Information changed from " +
        JSON.stringify([]) +
        " to " +
        JSON.stringify([formData]);
      message = "Customer Banner Information created";
      body = {
        message,
        summary: JSON.stringify(summaryString),
        type: "banner_info",
        code: "create",
        access_type: true,
        metadata: { changedBy: "" },
      };

      const res = await createNewBannerInfo(
        match.params.locationId,
        formData,
        body,
        "customer",
      );
      const cloneBannerList = _.cloneDeep(bannerList);
      cloneBannerList.push(res.data.data);
      setBannerList(cloneBannerList);
      setIsLoadingButton(false);
      setIsEdit(false);
      setIsOpenBannerAddEditModal(false);
      setBannerId("");
      setTitle("");
      setSubTitle("");
      setDescription("");
      setImg("");
      setIsStatus(true);
      setOrder(0);
      setOriginalNodeData({ availability: [] });
      setSuccess(SUCCESSFULLY_CREATED);
    } catch (error) {
      setIsError(true);
    }
  };

  const handleUpdateBanner = async () => {
    try {
      const formData = {
        bannerId,
        title: title,
        subTitle: subTitle,
        description: description,
        img: img,
        status: isStatus,
        order,
        availability: originalNodeData.availability,
      };
      let message = "";
      let body = {};
      let summaryString: any = [];
      const filter = bannerList.filter(
        (data: any) => data.bannerId === bannerId,
      );
      if (getObjectDifferences(filter[0], formData)) {
        summaryString = getObjectDifferences(filter[0], formData);
      }

      message = "Eatpresto Banner Information Changed";
      body = {
        message,
        summary: JSON.stringify(summaryString),
        type: "banner_info",
        code: "update",
        access_type: true,
        metadata: { changedBy: "" },
      };
      const res = await updateBannerInfo(
        match.params.locationId,
        bannerId,
        formData,
        body,
        "customer",
      );
      const cloneBannerList = _.cloneDeep(bannerList);
      const {
        title: resTitle,
        subTitle: resSubTitle,
        description: resDescription,
        img: resImg,
        status: resStatus,
        order: resOrder,
      } = res.data.data;
      cloneBannerList.map((data: any) => {
        if (data.bannerId === bannerId) {
          data["title"] = resTitle;
          data["subTitle"] = resSubTitle;
          data["description"] = resDescription;
          data["img"] = resImg;
          data["status"] = resStatus;
          data["order"] = resOrder;
        }
      });
      setBannerList(cloneBannerList);
      setIsLoadingButton(false);
      setIsEdit(false);

      setBannerId("");
      setTitle("");
      setSubTitle("");
      setDescription("");
      setImg("");
      setIsStatus(true);
      setOrder(0);
      setIsOpenBannerAddEditModal(false);
      setOriginalNodeData({ availability: [] });
      setSuccess(SUCCESSFULLY_UPDATED);
    } catch (error) {
      setIsError(true);
    }
  };
  const [availabilityError, setAvailabilityError] = useState(false);
  const handleSubmitData = () => {
    if (!title) {
      setIsError(true);
    } else {
      setIsLoadingButton(true);
      setIsError(false);
      if (isEdit) {
        handleUpdateBanner();
      } else {
        handleCreateBanner();
      }
    }
  };

  const handleEditBannerDetails = (banner: any) => {
    const { bannerId, title, subTitle, description, img, status } = banner;
    setBannerId(bannerId);
    setTitle(title);
    setSubTitle(subTitle);
    setDescription(description);
    setImg(img);
    setIsEdit(true);
    setOrder(banner?.order || 0);
    setIsOpenBannerAddEditModal(true);
    setIsStatus(banner?.status?.toString() === "true" ? true : false);
    setOriginalNodeData({ availability: banner?.availability || [] });
  };
  const handleDeleteBannerInfo = async () => {
    try {
      let message = "";
      let body = {};
      let summaryString = "";
      summaryString = "Removed by " + bannerId + " Banner Information.";
      message = "Customer Banner Information deleted";
      body = {
        message,
        summary: summaryString,
        type: "banner_info",
        code: "delete",
        access_type: true,
        metadata: { changedBy: "" },
      };
      const res = await deleteBannerInfo(
        match.params.locationId,
        bannerId,
        body,
        "customer",
      );
      const cloneBannerList = _.cloneDeep(bannerList);
      const filterCloneBannerList = cloneBannerList.filter(
        (data: any) => data.bannerId !== res.data.data.bannerId,
      );
      setBannerList(filterCloneBannerList);
      setIsLoadingButton(false);
      setIsEdit(false);
      setIsOpenBannerAddEditModal(false);
      setBannerId("");
      setSuccess(SUCCESSFULLY_DELETED);
      setIsOpenDeleteConfirmation(false);
    } catch (error) {
      setIsError(true);
    }
  };
  const handleDeleteBannerDetails = (bannerId: any) => {
    setBannerId(bannerId);
    setIsOpenDeleteConfirmation(true);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        {/* Filter Section with Autocomplete */}
        <Grid container>
          <Grid item xs={12} md={4}>
            <Autocomplete
              id="banner-filter-autocomplete"
              color="inherit"
              value={selectedFilter}
              options={filterOptions}
              getOptionLabel={(option: any) => option.label}
              disableClearable
              onChange={(event, newValue) => handleFilterChange(newValue)}
              style={{ zIndex: 0 }}
              renderOption={(props) => {
                return (
                  <Grid
                    container
                    {...props}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      zIndex: 0,
                      height: "20px",
                      marginBottom: "4px",
                    }}
                  >
                    <Grid item xs={11}>
                      <Typography
                        variant="body2"
                        className={classes.titleColor}
                      >
                        {props.label}
                      </Typography>
                    </Grid>
                  </Grid>
                );
              }}
              classes={{ root: classes.autoComplete }}
              renderInput={(params: any) => (
                <TextField
                  color="inherit"
                  {...params}
                  label="Filter"
                  style={{
                    borderRadius: 4,
                    width: "280px",
                  }}
                  variant="outlined"
                />
              )}
            />
          </Grid>
        </Grid>

        <CreateNewBanner
          handleOpenBannerAddModal={handleOpenBannerAddModal}
          isOwner={isOwner}
        />
      </div>
      {bannerList && <BannerHeader />}

      <Grid container>
        {filteredBannerList && filteredBannerList.length > 0 ? (
          filteredBannerList.map((bannerInfo: any) => (
            <Grid
              item
              xs={12}
              key={bannerInfo.bannerId}
              style={{ marginTop: "8px" }}
            >
              <BannerInfoNode
                banner={bannerInfo}
                handleEditBannerDetails={handleEditBannerDetails}
                handleDeleteBannerDetails={handleDeleteBannerDetails}
                isOwner={isOwner}
              />
            </Grid>
          ))
        ) : (
          <Typography style={{ marginTop: "12%" }} variant="h5">
            {bannerList && bannerList.length > 0
              ? "No banners match the selected filter"
              : "No Banner"}
          </Typography>
        )}
      </Grid>

      <BannerCreateAndUpdateModal
        isOpen={isOpenBannerAddEditModal}
        setIsOpen={setIsOpenBannerAddEditModal}
        isEdit={isEdit}
        handleReset={handleReset}
        isLoadingButton={isLoadingButton}
        handleSubmitData={handleSubmitData}
        title={title}
        subTitle={subTitle}
        description={description}
        img={img}
        isError={isError}
        setIsError={setIsError}
        setTitle={setTitle}
        setSubTitle={setSubTitle}
        setDescription={setDescription}
        setImg={setImg}
        setIsStatus={setIsStatus}
        isStatus={isStatus}
        setOrder={setOrder}
        order={order}
        setSuccess={setSuccess}
        setError={setError}
        setOriginalNodeData={setOriginalNodeData}
        originalNodeData={originalNodeData}
        availabilityError={availabilityError}
        bannerId={bannerId}
        bannerList={bannerList}
        setBannerList={setBannerList}
      />

      <DefaultAlert
        open={!!success}
        handleClose={() => setSuccess("")}
        message={success}
        severity={"success"}
      />

      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />

      <ConfirmDeleteDialog
        open={isOpenDeleteConfirmation}
        setOpen={setIsOpenDeleteConfirmation}
        confirmAction={handleDeleteBannerInfo}
      />
    </>
  );
};

export default WithLoading(BannerNode);
